import { JSXElementConstructor, ReactElement, useState } from 'react'

import { classifyAttachments } from '../../hooks/useAttachmentTypes'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useToasts } from '@/hooks/useToasts'
import { DocumentProps, pdf } from '@react-pdf/renderer'
import * as Sentry from '@sentry/nextjs'
import { useSelector } from 'react-redux'

import { useApolloClient } from '@apollo/client'
import fontkit from '@pdf-lib/fontkit'
import downloadjs from 'downloadjs'
import { PDFDocument } from 'pdf-lib'

import {
  OrganizationDocument,
  OrganizationReportBrandingDocument,
  UsersDocument,
} from '@/graphql/generated/hooks'
import { selectMe } from '@/redux/me/meSlice'
import { BUTTON_PRESS, mixpanel } from '@/utils/analytics'
import { formatDateTime } from '@/utils/formatDateTime'
import { abbreviateName } from '@/utils/formats'

import { ReportTemplate } from '../components/ReportTemplate'
import { DownloadReportButtonIProps, ReportBrandingI } from '../types/types'
import { createPDFTextField, getFieldAppearanceOptions } from '../utils/utils'

const EDITABLE_FIELD_HORIZONTAL_OFFSET = 50
const EDITABLE_FIELD_HEIGHT = 135
const SUMMARY_FIELD_TOP_OFFSET = 224
const ACTIONS_FIELD_TOP_OFFSET = 407

export const useReportDownload = ({
  incident,
  workflowReportData,
}: DownloadReportButtonIProps) => {
  const { showError } = useToasts()
  const [isLoading, setIsLoading] = useState(false)
  const client = useApolloClient()
  const me = useSelector(selectMe)
  const { salesDatabaseReset: isSalesEnvironment } = useFeatureFlags()

  const getWorkflowUserNameMap = async () => {
    const userIds = [
      ...new Set(
        (workflowReportData?.workflowActions ?? [])
          .map((action) => action.userId)
          .filter((userId): userId is string => Boolean(userId))
      ),
    ]

    if (userIds.length === 0) return new Map<string, string>()

    try {
      const { data } = await client.query({
        fetchPolicy: 'cache-first',
        query: UsersDocument,
        variables: { filter: { idIn: userIds } },
      })

      const userNameMap = new Map<string, string>()

      for (const edge of data?.users?.edges ?? []) {
        if (!edge?.node) continue

        const { email, firstName, id, lastName } = edge.node
        const fullName = [firstName, lastName].filter(Boolean).join(' ')
        const displayName = fullName ? abbreviateName(fullName) : email

        if (displayName) {
          userNameMap.set(id, displayName)
        }
      }

      return userNameMap
    } catch (error) {
      Sentry.captureException(error)
      return new Map<string, string>()
    }
  }

  /**
   * Org name + logo for the report header. logoUrl is a presigned URL with a
   * 30-minute expiry, so this is fetched fresh (no-cache) at generation time.
   * Deploy skew: a backend without the logoUrl field rejects the whole
   * operation, so on ANY failure fall back to the legacy name-only query and
   * render the text-only header — the report must still download.
   */
  const getOrganizationBranding = async (): Promise<ReportBrandingI> => {
    try {
      const { data } = await client.query({
        fetchPolicy: 'no-cache',
        query: OrganizationReportBrandingDocument,
      })

      return {
        orgName: data?.currentOrganization?.name ?? '',
        logoUrl: data?.currentOrganization?.logoUrl ?? null,
      }
    } catch (error) {
      // Warning, not error: this is the expected path when the deployed
      // backend predates the logoUrl field, and the report still downloads
      // via the fallback below. Visible in Sentry without firing alerts.
      Sentry.captureException(error, {
        level: 'warning',
        tags: { action: 'fetch_report_branding', feature: 'incident_drawer' },
      })
    }

    try {
      const { data } = await client.query({
        fetchPolicy: 'cache-first',
        query: OrganizationDocument,
      })

      return { orgName: data?.currentOrganization?.name ?? '', logoUrl: null }
    } catch (error) {
      Sentry.captureException(error)
      return { orgName: '', logoUrl: null }
    }
  }

  const getReportTemplate = async () => {
    const exportDate = new Date()
    // Short code ("PDT"), not the IANA name — keeps the footer strip compact.
    const exportTimeZone =
      new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
        .formatToParts(exportDate)
        .find((part) => part.type === 'timeZoneName')?.value ?? ''
    const fullName = [me?.firstName, me?.lastName].filter(Boolean).join(' ')
    const username = fullName || me?.username || me?.email || ''
    const [branding, workflowUserNameMap, attachments] = await Promise.all([
      getOrganizationBranding(),
      getWorkflowUserNameMap(),
      classifyAttachments(incident?.imageUrls),
    ])

    return ReportTemplate({
      attachments,
      branding,
      exportDetails: {
        dateExported: formatDateTime(exportDate, 'MM-dd-yyyy'),
        timeZone: exportTimeZone,
        timeExported: formatDateTime(exportDate, 'HH:mm'),
        username,
      },
      incident,
      isSalesEnvironment,
      me,
      workflowReportData,
      workflowUserNameMap,
    })
  }

  const handleReportDownload = async (
    template: ReactElement<DocumentProps, string | JSXElementConstructor<any>>,
    reportName: string
  ) => {
    const blob = await pdf(template).toBlob()

    const formPdfBytes = await blob.arrayBuffer()
    const pdfDoc = await PDFDocument.load(formPdfBytes)

    pdfDoc.registerFontkit(fontkit)

    const form = pdfDoc.getForm()

    const pages = pdfDoc.getPages()

    const summaryField = createPDFTextField(form, 'summary', '')
    const actionsField = createPDFTextField(form, 'actions', '')

    const lastPage = pages[pages.length - 1]
    // Keep these pdf-lib overlays aligned with the React PDF layout in EditableFields.
    const summaryFieldY = lastPage.getHeight() - SUMMARY_FIELD_TOP_OFFSET
    const actionsFieldY = lastPage.getHeight() - ACTIONS_FIELD_TOP_OFFSET

    summaryField.addToPage(
      lastPage,
      getFieldAppearanceOptions(
        lastPage,
        EDITABLE_FIELD_HORIZONTAL_OFFSET,
        summaryFieldY,
        EDITABLE_FIELD_HEIGHT
      )
    )

    actionsField.addToPage(
      lastPage,
      getFieldAppearanceOptions(
        lastPage,
        EDITABLE_FIELD_HORIZONTAL_OFFSET,
        actionsFieldY,
        EDITABLE_FIELD_HEIGHT
      )
    )

    const sohne = await pdfDoc.embedFont(
      await fetch('/fonts/Sohne/soehne-buch.ttf').then((res) =>
        res.arrayBuffer()
      )
    )

    actionsField.updateAppearances(sohne)
    summaryField.updateAppearances(sohne)
    summaryField.setFontSize(11)
    actionsField.setFontSize(11)

    const pdfBytes = await pdfDoc.save()

    downloadjs(pdfBytes, reportName, 'application/pdf')
  }

  const onDownloadComplete = () => {
    mixpanel.track(`${BUTTON_PRESS} Incident Report Download`, {
      user_id: me?.id,
      download_time: new Date().toISOString(),
    })
  }

  const downloadReport = async () => {
    setIsLoading(true)

    try {
      const template = await getReportTemplate()
      await handleReportDownload(
        template,
        `${incident?.displayId}-${incident?.name} Report.pdf`
      )
      setIsLoading(false)
      onDownloadComplete()
    } catch (error) {
      Sentry.captureException(error)
      setIsLoading(false)
      showError('Download Failed. Please try again later')
    }
  }

  return { isLoading, handleReportDownload, downloadReport }
}
